/**
 * @fileoverview The plant a tapped row opens over the page, growing or removed, read by its id when it is tapped
 * (W24 Job H). Beds (a bed's plant list and its Lately), Seasons and Season review (a diary row) used to download the
 * whole archive when they opened, so that any row could open its plant; now a plant in the garden list opens at once,
 * any other id is read (GET /plants?plantObjId=N, which answers the plant whatever its status), then opened, and a read
 * that fails goes to the plant's own page (/plants/:id), as a plant in neither list did before.
 *
 * The plant read by its id is kept as a one-plant list under the plants' key (['plants', 'byId', id, zip, language]), so every writer that brings the garden's plants in step in place (a photo, a note, the Public switch,
 * updateSinglePlant) or reads them again (an invalidation of 'plants') keeps this one in step too: a photo added from
 * inside the quick look shows on it.
 *
 * Where: features/locations LocationGrid, features/season SeasonPage.
 * API: GET /plants?plantObjId=&zip=&lang= (getSinglePlant), only for a plant not in the garden list.
 * Settings: none; the key starts with the plants' own word (PLANT_BY_ID_KEY_ROOT, the same as PLANTS_QUERY_KEY), with
 * 'byId' after it.
 *
 * @module usePlantById
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getSinglePlant } from '@/shared/api/plants';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Plant } from '@/shared/types/plant';

/** The plant open over the page, and the way to open and close it. */
export interface PlantById {
  /** The plant open, or null: none is open, or the one tapped is still being read. */
  plant: Plant | null;
  /**
   * Opens a plant by its id: at once from the garden list, else once it is read; a failed read goes to its own page.
   *
   * @param plantObjId - The plant tapped.
   */
  open: (plantObjId: number) => void;
  /** Closes it. */
  close: () => void;
}

/**
 * The first word of the key: the plants' own ('plants', PLANTS_QUERY_KEY in config/tunables/ui, which this file may not
 * read: scripts/check-value-files.mjs), so every writer and invalidation of the garden's plants reaches this one too.
 * @kept K1
 */
const PLANT_BY_ID_KEY_ROOT = 'plants';

/**
 * The key a plant read by its id is kept under: the plants' key first, so the garden's writers reach it.
 *
 * @param plantObjId - The plant.
 * @param zip - The garden's postcode, as the plant's crop temperatures are read with it.
 * @param language - The language its names come in.
 * @returns The key.
 */
export function plantByIdKey(plantObjId: number | null, zip: string, language: string): readonly unknown[] {
  return [PLANT_BY_ID_KEY_ROOT, 'byId', plantObjId, zip || 'none', language];
}

/**
 * The plant a tapped row opens over the page, growing or removed.
 *
 * @param garden - The garden's growing plants as the page has them (usePlantsContext), or nothing before they arrive.
 * @returns The plant open, and open and close.
 */
export function usePlantById(garden: Plant[] | null | undefined): PlantById {
  const navigate = useNavigate();
  const { i18n: { language } } = useTranslation();
  const { zip } = useGardenPostcode();
  const [openedId, setOpenedId] = React.useState<number | null>(null);
  /** The plant open, when the garden list has it. */
  const fromGarden = openedId === null ? null : garden?.find((plant) => plant.plant_obj_id === openedId) ?? null;
  const { data, isError } = useQuery({
    queryKey: plantByIdKey(openedId, zip, language),
    queryFn: async (): Promise<Plant[]> => [await getSinglePlant(String(openedId), zip || undefined, language)],
    enabled: openedId !== null && fromGarden === null,
    retry: false,
  });
  // A plant that could not be read (gone, or not this garden's): its own page says why, as before.
  React.useEffect(() => {
    if (!isError || openedId === null || fromGarden !== null) return;
    setOpenedId(null);
    navigate(`/plants/${openedId}`);
  }, [isError, openedId, fromGarden, navigate]);
  const open = React.useCallback((plantObjId: number) => setOpenedId(plantObjId), []);
  const close = React.useCallback(() => setOpenedId(null), []);
  return { plant: fromGarden ?? data?.[0] ?? null, open, close };
}
